import {
  AnimatePresence,
  useReducedMotion,
  type Transition
} from "framer-motion";
import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import {
  alertPanelVariants,
  enterTransition,
  exitTransition,
  overlayVariants,
  panelVariants,
  recedeTransition,
  REDUCED_MOTION_TRANSITION
} from "./modalConstants";
import {
  DragHandle,
  StyledAlertModal,
  StyledCenterModal,
  StyledDialog,
  StyledOverlay,
  StyledSheetModal
} from "./Modal.styled";
import { ModalGlobalStyles } from "./ModalGlobalStyles";
import { useCardModalBreakpoint } from "./useCardModalBreakpoint";
import { useModalStack } from "./useModalStack";
import { useSheetDrag } from "./useSheetDrag";

export type ModalVariant = "center" | "sheet" | "alert";

export interface ModalProps {
  isOpen: boolean;
  /** Called just before the dismiss animation begins. */
  onWillDismiss?: () => void;
  /** Called after the dismiss animation completes (matches IonModal semantics). */
  onDidDismiss?: () => void;
  variant?: ModalVariant;
  /** Default true. When false, neither backdrop click nor Escape will dismiss. */
  backdropDismiss?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Style on the overlay — use to set CSS custom props (`--height`, `--width`, etc.). */
  style?: CSSProperties;
  /** Extra data-* attributes set on the overlay (for styling hooks). */
  dataAttributes?: Record<string, string | boolean | undefined>;
  children: ReactNode;
}

export interface ModalHandle {
  dismiss: () => void;
}

type CenterAnimateState = "open" | "receded";

function pickTransition(
  reduced: boolean,
  exiting: boolean,
  receded: boolean
): Transition {
  if (reduced) {
    return REDUCED_MOTION_TRANSITION;
  }
  if (receded) {
    return recedeTransition;
  }
  return exiting ? exitTransition : enterTransition;
}

const Modal = forwardRef<ModalHandle, ModalProps>(function Modal(
  {
    isOpen,
    onWillDismiss,
    onDidDismiss,
    variant = "center",
    backdropDismiss = true,
    className,
    ariaLabel,
    style,
    dataAttributes,
    children
  },
  ref
) {
  const isMobile = useCardModalBreakpoint();
  const prefersReducedMotion = useReducedMotion() ?? false;

  // Drives AnimatePresence's exit independently of the parent's `isOpen` — when
  // dismiss() fires we flip this immediately so the exit plays without waiting
  // for the parent to react.
  const [isExiting, setIsExiting] = useState(false);
  const shouldRender = isOpen && !isExiting;

  // Tying the stack to `shouldRender` (not `isOpen`) starts the host app's
  // un-recede in sync with this modal's exit animation.
  const { zIndex, isTopmost } = useModalStack(shouldRender, variant);

  // Reentrancy guard — consumers commonly call dismiss() from their own
  // onClose handler.
  const dismissingRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      dismissingRef.current = false;
      setIsExiting(false);
    }
  }, [isOpen]);

  const dismiss = useCallback(() => {
    if (dismissingRef.current) {
      return;
    }
    dismissingRef.current = true;
    onWillDismiss?.();
    setIsExiting(true);
  }, [onWillDismiss]);

  const handleExitComplete = useCallback(() => {
    onDidDismiss?.();
  }, [onDidDismiss]);

  useImperativeHandle(ref, () => ({ dismiss }), [dismiss]);

  const { dragY, handlePointerDown } = useSheetDrag({ onDismiss: dismiss });

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      dismiss();
    }
  };

  const centerAnimate: CenterAnimateState =
    isMobile && !isTopmost ? "receded" : "open";

  const overlayTransition = pickTransition(
    prefersReducedMotion,
    isExiting,
    false
  );
  const centerTransition = pickTransition(
    prefersReducedMotion,
    isExiting,
    centerAnimate === "receded"
  );
  const sheetTransition = pickTransition(
    prefersReducedMotion,
    isExiting,
    false
  );
  const alertTransition = pickTransition(
    prefersReducedMotion,
    isExiting,
    false
  );

  const motionPanelProps = {
    variants: panelVariants,
    initial: "closed",
    exit: "closed"
  } as const;

  return (
    <>
      <ModalGlobalStyles />
      <AnimatePresence onExitComplete={handleExitComplete}>
        {shouldRender && (
          <StyledOverlay
            isOpen
            onOpenChange={handleOpenChange}
            isDismissable={backdropDismiss}
            isKeyboardDismissDisabled={!backdropDismiss}
            data-nine-modal=""
            data-variant={variant}
            $zIndex={zIndex}
            style={style}
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            transition={overlayTransition}
            {...dataAttributes}
          >
            {variant === "sheet" ? (
              <StyledSheetModal
                className={className}
                {...motionPanelProps}
                animate="open"
                transition={sheetTransition}
                style={{ y: dragY }}
              >
                <DragHandle
                  onPointerDown={handlePointerDown}
                  role="button"
                  aria-label="Drag to dismiss"
                />
                <StyledDialog aria-label={ariaLabel}>{children}</StyledDialog>
              </StyledSheetModal>
            ) : variant === "alert" ? (
              <StyledAlertModal
                className={className}
                variants={alertPanelVariants}
                initial="closed"
                animate="open"
                exit="closed"
                transition={alertTransition}
              >
                <StyledDialog aria-label={ariaLabel}>{children}</StyledDialog>
              </StyledAlertModal>
            ) : (
              <StyledCenterModal
                className={className}
                {...motionPanelProps}
                animate={centerAnimate}
                transition={centerTransition}
              >
                <StyledDialog aria-label={ariaLabel}>{children}</StyledDialog>
              </StyledCenterModal>
            )}
          </StyledOverlay>
        )}
      </AnimatePresence>
    </>
  );
});

export default Modal;
